<script lang="ts">
  /**
   * The improve step: eleven keywords chosen for the community, and the three tables
   * they were chosen from.
   *
   * It picks on MOUNT, not on a click. The reader has just been told where they rank;
   * the next useful thing is the list to run with, and making them press a button for
   * it is asking them to request the answer they came for. A caller mounts this at the
   * moment the reader asks to improve, so each visit starts from empty picks and a
   * fresh suggestion.
   */
  import {
    chooseKeywords,
    discoveryUrl,
    fetchLeaders,
    TARGET,
    volumeColor,
    volumeLabel,
    volumeWidth,
    type Leader,
  } from '../../audit';
  import type { AuditRun } from '../../audit/run.svelte';
  import LeaderStrip from './LeaderStrip.svelte';

  let { run, url }: { run: AuditRun; url: string } = $props();

  // Skool accepts eleven discovery keywords, so eleven is the real shape of the
  // decision — this is not a wishlist, it is the list the owner pastes back.
  const LIMIT = 11;
  // How many rows each table SHOWS, which is a different question. Pricing every
  // candidate turned the winnable list from about eleven rows into seventy, and showing
  // only as many as could be selected hid most of the analysis — keywords with real
  // competitor concentration sat just below the fold. Kept in step with
  // `DISPLAYED_PER_TABLE` in `audit/runner.py`, which decides how many get preloaded; if
  // they drift, the rows past the preload go cold and hover slowly.
  const ROWS_SHOWN = 20;
  // Keyed by keyword: the same term can appear in more than one table, and ticking it
  // in one place must tick it everywhere.
  let picked = $state<Record<string, boolean>>({});
  const chosen = $derived(Object.keys(picked).filter((k) => picked[k]));
  const full = $derived(chosen.length >= LIMIT);

  function toggle(keyword: string): void {
    if (!picked[keyword] && full) return; // at the cap, ticking another does nothing
    picked = { ...picked, [keyword]: !picked[keyword] };
  }

  let choosing = $state(false);
  // Guards the auto-pick so it runs once rather than on every re-render.
  let autoPicked = $state(false);

  // `recs` may land after the step opens, so this waits for it rather than firing on
  // mount.
  $effect(() => {
    if (run.recs && !autoPicked && !choosing) {
      autoPicked = true;
      void autoPick();
    }
  });

  // Top-five communities per keyword, filled in the first time a row is pointed at.
  // Cached because a mouse crossing three tables would otherwise re-ask for every row
  // it passes over, and an in-flight marker because it crosses faster than a fetch.
  let leaders = $state<Record<string, Leader[]>>({});
  const pending = new Set<string>();

  async function loadLeaders(keyword: string): Promise<void> {
    if (leaders[keyword] || pending.has(keyword)) return;
    pending.add(keyword);
    try {
      // Same language as the table above, or the hover contradicts the row it
      // belongs to.
      const found = await fetchLeaders(url.trim(), keyword, run.community?.language);
      leaders = { ...leaders, [keyword]: found };
    } catch {
      // A row that cannot load its competitors keeps its numbers; the hover just
      // stays empty rather than showing an error over a table.
    } finally {
      pending.delete(keyword);
    }
  }

  // Every row on the screen, deduped by keyword — the same term can sit in more than
  // one table, and the model must be offered it once.
  const candidates = $derived.by(() => {
    const seen = new Map<string, { keyword: string; n_hits: number; used_by: number; current: boolean }>();
    for (const s of run.standings) {
      seen.set(s.keyword, {
        keyword: s.keyword, n_hits: s.n_hits,
        used_by: run.support[s.keyword] ?? 0, current: true,
      });
    }
    for (const c of [...(run.recs?.winnable ?? []), ...(run.recs?.high_volume ?? [])]) {
      if (!seen.has(c.keyword)) {
        seen.set(c.keyword, {
          keyword: c.keyword, n_hits: c.n_hits, used_by: c.used_by, current: false,
        });
      }
    }
    return [...seen.values()];
  });

  // The chosen rows, and the split the summary reports. Band is decided by field size
  // (`TARGET`), not by which table a keyword was ticked in — a current keyword with
  // 1,600 results is a high-volume pick wherever it was clicked.
  const chosenRows = $derived(
    chosen
      .map((k) => candidates.find((c) => c.keyword === k))
      .filter((c): c is { keyword: string; n_hits: number; used_by: number; current: boolean } => !!c),
  );
  const highCount = $derived(chosenRows.filter((c) => c.n_hits >= TARGET).length);
  const lowCount = $derived(chosenRows.length - highCount);

  async function autoPick(): Promise<void> {
    if (choosing || candidates.length === 0) return;
    choosing = true;
    try {
      // Same language as the audit: the model is picking terms this community's
      // own members would search for, not English ones.
      const keep = await chooseKeywords(url.trim(), candidates, run.community?.language);
      picked = Object.fromEntries(keep.map((k) => [k, true]));
    } catch {
      // Silent: the button is a shortcut, and the reader can still tick boxes.
    } finally {
      choosing = false;
    }
  }
</script>

{#snippet picker(title: string, rows: { keyword: string; n_hits: number; rank: number | null; used_by: number }[])}
  <section>
    <h3 class="eyebrow mb-2">{title}</h3>
    {#if rows.length === 0}
      <p class="rounded-box border border-base-300 p-4 text-sm text-base-content/50">
        Nothing here.
      </p>
    {:else}
      <div class="audit-table rounded-box border border-base-300 bg-base-100">
        <table class="table table-xs sm:table-sm stack-sm">
          <thead>
            <tr>
              <th class="w-8 px-2"><span class="sr-only">Selected</span></th>
              <th class="px-1">Keyword</th>
              <th class="w-16 px-1 text-right">Your rank</th>
              <th class="w-24 px-1 text-right">Search Results</th>
              <th class="w-16 px-2 text-right">Competitors</th>
            </tr>
          </thead>
          <tbody>
            {#each rows.slice(0, ROWS_SHOWN) as row (row.keyword)}
              {@const on = !!picked[row.keyword]}
              <tr>
                <td class="px-2" data-cell="check">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-sm"
                    checked={on}
                    disabled={!on && full}
                    onchange={() => toggle(row.keyword)}
                    aria-label={row.keyword}
                  />
                </td>
                <td
                  data-cell="kw"
                  class="group relative cursor-pointer px-1 font-medium"
                  onmouseenter={() => loadLeaders(row.keyword)}
                >
                  <span class:opacity-40={!on && full}>{row.keyword}</span><a
                    class="ml-1 text-base-content/30 transition-colors hover:text-primary"
                    href={discoveryUrl(row.keyword, run.community?.language)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Open Skool search for "${row.keyword}"`}
                    onclick={(e) => e.stopPropagation()}
                    aria-label={`Open Skool search for ${row.keyword}`}>&rarr;</a
                  >
                  <!-- Opens downward and to the left edge of the cell: these
                       tables are a third of the width, so a card anchored to
                       the pointer would leave the column. -->
                  <LeaderStrip leaders={leaders[row.keyword]} />
                </td>
                <td
                  data-cell="rank"
                  class="cursor-pointer px-1 text-right tabular-nums"
                  class:opacity-40={!on && full}
                  title={row.rank === null
                    ? 'Not ranking in the top 1000 for this keyword'
                    : `You are #${row.rank} for this keyword`}
                >
                  {#if row.rank === null}
                    <span class="text-xs text-base-content/40">unranked</span>
                  {:else}
                    <span class="font-semibold">#{row.rank}</span>
                  {/if}
                </td>
                <td class="px-1 text-right" data-cell="size">
                  <div
                    class="flex cursor-pointer flex-col items-end gap-1"
                    class:opacity-40={!on && full}
                    title={volumeLabel(row.n_hits)}
                  >
                    <span class="tabular-nums">{row.n_hits.toLocaleString()}</span>
                    <div class="h-1.5 w-full rounded-full bg-base-300">
                      <div
                        class="h-full rounded-full {volumeColor(row.n_hits)}"
                        style="width: {volumeWidth(row.n_hits)}%"
                      ></div>
                    </div>
                  </div>
                </td>
                <td
                  class="px-2 text-right tabular-nums"
                  class:opacity-40={!on && full}
                  data-cell="comp"
                  data-label="competitors">{row.used_by || '—'}</td
                >
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </section>
{/snippet}

<!-- The answer, as the page's own hero: eleven keywords already chosen, each
     carrying the field size that justified it. Sticky only once the tables are
     open, because until then it IS the page and has nothing to stick above. -->
<div
  class="mx-auto mb-4 w-full max-w-4xl rounded-box border border-base-300
         bg-base-200 p-4 sm:p-5"
>
  {#if choosing || (!run.recs && chosen.length === 0)}
    <div class="flex items-center gap-3 py-2 text-base-content/60">
      <span class="loading loading-spinner loading-sm"></span>
      Choosing your eleven…
    </div>
  {:else if chosen.length === 0}
    <p class="py-2 text-sm text-base-content/60">
      Nothing selected. Pick from the tables below.
    </p>
  {:else}
    <!-- One line, and only the fact the heading above does not already carry.
         An eyebrow naming the box under an h2 naming the step was a header for
         a header. -->
    <p class="mb-3 text-sm text-base-content/70">
      <span class="tabular-nums">{highCount}</span> high volume
      <span class="mx-1 text-base-content/30">+</span>
      <span class="tabular-nums">{lowCount}</span> winnable niches
    </p>
    <div class="flex flex-wrap gap-1.5">
      {#each chosen as keyword (keyword)}
        {@const row = chosenRows.find((c) => c.keyword === keyword)}
        <!-- The keyword itself is the control, not the pill. A <span> carrying
             the handlers needed a role and a key handler to be reachable, and
             wrapping the whole thing in a <button> would have nested the remove
             button inside it. A real button for the reveal, a real button for
             the remove, and the pill stays a container. -->
        <span
          class="group relative inline-flex min-w-0 grow basis-[calc(50%-0.375rem)] flex-col
                 gap-1 rounded-box border border-base-300 bg-base-100 px-2.5 py-1
                 sm:grow-0 sm:basis-auto"
        >
          <span class="flex items-center gap-1.5 text-xs">
            <button
              type="button"
              class="flex min-w-0 flex-1 cursor-pointer flex-wrap items-center gap-x-1.5 text-left"
              onmouseenter={() => loadLeaders(keyword)}
              onfocus={() => loadLeaders(keyword)}
            >
              <span class="min-w-0 font-medium [overflow-wrap:normal] [word-break:normal]">{keyword}</span>
              {#if row}
                <span class="shrink-0 whitespace-nowrap tabular-nums opacity-60"
                  >{row.n_hits.toLocaleString()}</span
                >
              {/if}
            </button>
            <button
              type="button"
              class="opacity-50 hover:opacity-100"
              aria-label={`Remove ${keyword}`}
              onclick={() => toggle(keyword)}>&times;</button
            >
          </span>
          {#if row}
            <!-- The same banded bar the tables carry, so a pill and its row read
                 identically rather than making the reader hold a number in mind. -->
            <span class="mb-0.5 block h-[3px] w-full rounded-full bg-base-300">
              <span
                class="block h-full rounded-full {volumeColor(row.n_hits)}"
                style="width: {volumeWidth(row.n_hits)}%"
              ></span>
            </span>
          {/if}
          <LeaderStrip leaders={leaders[keyword]} />
        </span>
      {/each}
    </div>
  {/if}
</div>

<!-- Green at eleven, grey below it. Reaching the cap is the goal of this
     screen, not a limit being hit — red said "stop" about the one state the
     reader is aiming for. -->
<!-- A ternary rather than `class:`: Svelte's class directive cannot take a
     name containing a slash, and `text-base-content/50` has one. -->
<p
  class="mb-5 text-center text-xs font-bold tabular-nums {full
    ? 'text-success'
    : 'text-base-content/50'}"
>
  {chosen.length}/{LIMIT} keywords
</p>

<!-- Under the list it re-rolls, not up in the title. It acts on what is in that
     box, so it belongs beside it. The tables below are the manual path, always
     open, so this is the only control between the two. -->
<div class="mb-8 flex justify-center">
  <button
    class="btn btn-primary w-full sm:w-auto"
    disabled={choosing || !run.recs || candidates.length === 0}
    onclick={autoPick}
  >
    {#if choosing}
      <span class="loading loading-spinner loading-sm"></span>
    {:else}
      <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M7.5 5.6 10 7l-2.5 1.4L6 11l-1.5-2.6L2 7l2.5-1.4L6 3l1.5 2.6ZM19 15l1.7 3L24 19.5l-3.3 1.5L19 24l-1.7-3L14 19.5l3.3-1.5L19 15ZM14.9 3.2 16 5.5l2.3 1.1-2.3 1.1L14.9 10l-1.1-2.3L11.5 6.6l2.3-1.1 1.1-2.3ZM12.8 10.6a1 1 0 0 0-1.4 0L2 20a1 1 0 0 0 0 1.4l.6.6a1 1 0 0 0 1.4 0l9.4-9.4a1 1 0 0 0 0-1.4l-.6-.6Z"/>
      </svg>
    {/if}
    Suggest again
  </button>
</div>

<div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
{@render picker(
  'Your keywords',
  run.standings.map((s) => ({
    keyword: s.keyword,
    n_hits: s.n_hits,
    rank: s.rank,
    used_by: run.support[s.keyword] ?? 0,
  })),
)}
{#if run.recs}
  {@render picker(
    'High volume',
    run.recs.high_volume.map((c) => ({ keyword: c.keyword, n_hits: c.n_hits, rank: c.rank, used_by: c.used_by })),
  )}
  {@render picker(
    'Winnable niches',
    run.recs.winnable.map((c) => ({ keyword: c.keyword, n_hits: c.n_hits, rank: c.rank, used_by: c.used_by })),
  )}
{:else}
  <!-- Opened before stage two landed. Two placeholders rather than one, so the
       grid keeps its three columns and nothing reflows when they arrive. -->
  {#each ['High volume', 'Winnable niches'] as title (title)}
    <section>
      <h3 class="eyebrow mb-2">{title}</h3>
      <div class="flex items-center gap-2 rounded-box border border-base-300 p-4
                  text-sm text-base-content/50">
        <span class="loading loading-spinner loading-xs"></span> Finding these…
      </div>
    </section>
  {/each}
{/if}
</div>
